import React, { useState, useRef, useEffect, useCallback } from 'react';
import browserGuardService from '../services/browserGuard';
import { extractSignals, haversineMeters, formatDistance, RULE_LABELS } from '../utils/geoSignals';
import { REASON_CODES, reasonName } from '../utils/reasonCodes';
import './ABTest.css';

// ── Aggregation ──────────────────────────────────────────────────────────────
function percentile(sorted, p) {
  if (!sorted.length) return null;
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}
function mean(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null; }
function mode(arr) {
  const c = {}; let best = null, bestN = 0;
  for (const v of arr) { if (v == null) continue; c[v] = (c[v] || 0) + 1; if (c[v] > bestN) { bestN = c[v]; best = v; } }
  return best;
}

function computeStats(sample) {
  const checks = sample.checks;
  const n = checks.length;
  const passes = checks.filter((c) => c.verdict === 'pass').length;
  const latencies = checks.map((c) => c.latencyMs).filter((v) => v != null).sort((a, b) => a - b);
  const accs = checks.map((c) => c.accuracy).filter((v) => v != null);
  const scores = checks.map((c) => c.proxyscore).filter((v) => v != null);
  const coords = checks.filter((c) => c.lat != null && c.lon != null).map((c) => ({ lat: c.lat, lon: c.lon }));

  // Centroid + jitter (mean distance from centroid).
  let centroid = null, jitter = null;
  if (coords.length) {
    centroid = { lat: mean(coords.map((c) => c.lat)), lon: mean(coords.map((c) => c.lon)) };
    const dists = coords.map((c) => haversineMeters(centroid, c)).filter((v) => v != null);
    jitter = dists.length ? Math.round(mean(dists)) : null;
  }

  // Risk-rule frequency across checks.
  const riskFreq = {};
  for (const c of checks) for (const r of (c.risks || [])) riskFreq[r.rule] = (riskFreq[r.rule] || 0) + 1;

  // error_code histogram.
  const errHist = {};
  for (const c of checks) { const k = c.errorCode ?? 'n/a'; errHist[k] = (errHist[k] || 0) + 1; }

  return {
    n,
    passes,
    passRate: n ? passes / n : 0,
    latency: { p50: percentile(latencies, 50), p95: percentile(latencies, 95), max: latencies[latencies.length - 1] ?? null, avg: latencies.length ? Math.round(mean(latencies)) : null },
    accuracy: accs.length ? Math.round(mean(accs)) : null,
    proxyscore: scores.length ? Math.round(mean(scores)) : null,
    centroid,
    jitter,
    city: mode(checks.map((c) => c.city)),
    state: mode(checks.map((c) => c.state)),
    riskFreq,
    errHist,
  };
}

function pct(x) { return `${Math.round(x * 100)}%`; }

export default function ABTest() {
  const [labelA, setLabelA] = useState('Baseline (e.g. VPN off)');
  const [labelB, setLabelB] = useState('Condition (e.g. VPN on)');
  const [samples, setSamples] = useState(5);
  const [intervalS, setIntervalS] = useState(3);
  const [reason, setReason] = useState('1'); // editable reason code (integer from the GeoComply sheet)

  const [sampleA, setSampleA] = useState(null);
  const [sampleB, setSampleB] = useState(null);
  const [capturing, setCapturing] = useState(null);   // 'A' | 'B' | null
  const [progress, setProgress] = useState(null);     // { side, done, total, phase }
  const [countdown, setCountdown] = useState(null);    // ms to next check while waiting
  const [note, setNote] = useState(null);

  const stopRef = useRef(false);
  const waitTimerRef = useRef(null);
  const resolveWaitRef = useRef(null);
  const nextAtRef = useRef(null);

  const clearWait = useCallback(() => {
    if (waitTimerRef.current) { clearTimeout(waitTimerRef.current); waitTimerRef.current = null; }
    if (resolveWaitRef.current) { const r = resolveWaitRef.current; resolveWaitRef.current = null; r(); }
  }, []);

  useEffect(() => () => { stopRef.current = true; clearWait(); }, [clearWait]);

  // Countdown ticker for the gap between checks.
  useEffect(() => {
    if (progress?.phase !== 'waiting') { setCountdown(null); return; }
    const upd = () => setCountdown(Math.max(0, (nextAtRef.current || 0) - Date.now()));
    upd();
    const id = setInterval(upd, 200);
    return () => clearInterval(id);
  }, [progress]);

  const wait = (ms) => new Promise((resolve) => {
    resolveWaitRef.current = resolve;
    waitTimerRef.current = setTimeout(() => { waitTimerRef.current = null; resolveWaitRef.current = null; resolve(); }, ms);
  });

  const runCheck = useCallback(async (reasonCode) => {
    if (!browserGuardService.isInitialized) {
      if (browserGuardService.userId) await browserGuardService.initialize(browserGuardService.userId);
      else throw new Error('BrowserGuard not initialized — log in first');
    }
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    await browserGuardService.requestGeolocation(reasonCode);
    const latencyMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
    const ev = (browserGuardService.geoEventLog || [])[0] || {};
    const sig = extractSignals(ev) || {};
    return { latencyMs, ...sig };
  }, []);

  const capture = useCallback(async (side) => {
    if (capturing) return;
    setNote(null);
    setCapturing(side);
    stopRef.current = false;
    const total = Math.max(1, Math.min(50, parseInt(samples, 10) || 1));
    const gapMs = Math.max(0, parseFloat(intervalS) || 0) * 1000;
    const checks = [];
    setProgress({ side, done: 0, total, phase: 'running' });
    try {
      for (let i = 0; i < total; i++) {
        if (stopRef.current) break;
        const rec = await runCheck(parseInt(reason, 10) || 1);
        checks.push(rec);
        setProgress({ side, done: i + 1, total, phase: 'running' });
        if (i < total - 1 && !stopRef.current && gapMs > 0) {
          nextAtRef.current = Date.now() + gapMs;
          setProgress({ side, done: i + 1, total, phase: 'waiting' });
          await wait(gapMs);
        }
      }
      if (checks.length) {
        const rc = parseInt(reason, 10) || 1;
        const sample = {
          label: side === 'A' ? labelA : labelB,
          params: { reason: reasonName(rc), reasonCode: rc, userId: browserGuardService.userId, samples: checks.length, intervalS: Number(intervalS) },
          checks,
          at: new Date().toLocaleString(),
        };
        if (side === 'A') setSampleA(sample); else setSampleB(sample);
      } else {
        setNote('No checks captured.');
      }
    } catch (e) {
      setNote(e.message || 'Capture failed');
    } finally {
      clearWait();
      setProgress(null);
      setCapturing(null);
    }
  }, [capturing, samples, intervalS, reason, labelA, labelB, runCheck, clearWait]);

  const stop = useCallback(() => { stopRef.current = true; clearWait(); }, [clearWait]);

  const reset = useCallback(() => { setSampleA(null); setSampleB(null); setNote(null); }, []);

  const exportJson = useCallback(() => {
    const payload = {
      experiment: 'BrowserGuard A/B comparison',
      operator: browserGuardService.operator || 'unknown',
      generatedAt: new Date().toISOString(),
      A: sampleA && { ...sampleA, stats: computeStats(sampleA) },
      B: sampleB && { ...sampleB, stats: computeStats(sampleB) },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `browserguard-ab-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sampleA, sampleB]);

  const statsA = sampleA ? computeStats(sampleA) : null;
  const statsB = sampleB ? computeStats(sampleB) : null;
  const bothDone = statsA && statsB && !capturing;

  return (
    <div className="ab-root">
      <div className="ab-intro">
        Compare BrowserGuard under two real-world conditions. Capture <b>A</b>, then change your
        condition (VPN, network, GPS spoofer…) and capture <b>B</b>. Only the condition should
        change — reason, user and sample size stay fixed.
      </div>

      {/* Setup */}
      <div className="ab-setup">
        <label className="ab-field ab-field--wide">
          <span>Condition A</span>
          <input value={labelA} onChange={(e) => setLabelA(e.target.value)} disabled={!!capturing} />
        </label>
        <label className="ab-field ab-field--wide">
          <span>Condition B</span>
          <input value={labelB} onChange={(e) => setLabelB(e.target.value)} disabled={!!capturing} />
        </label>
        <label className="ab-field">
          <span>Samples / side</span>
          <input type="number" min="1" max="50" value={samples} onChange={(e) => setSamples(e.target.value)} disabled={!!capturing} />
        </label>
        <label className="ab-field">
          <span>Interval (s)</span>
          <input type="number" min="0" max="120" value={intervalS} onChange={(e) => setIntervalS(e.target.value)} disabled={!!capturing} />
        </label>
        <label className="ab-field">
          <span>Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={!!capturing} title={reasonName(reason)}>
            {REASON_CODES.map((o) => <option key={o.code} value={o.code}>{`${o.code} — ${o.name}`}</option>)}
          </select>
        </label>
      </div>

      {/* Capture cards */}
      <div className="ab-cards">
        {['A', 'B'].map((side) => {
          const sample = side === 'A' ? sampleA : sampleB;
          const stats = side === 'A' ? statsA : statsB;
          const busy = capturing === side;
          return (
            <div key={side} className={`ab-card ab-card--${side.toLowerCase()}${sample ? ' ab-card--done' : ''}`}>
              <div className="ab-card-head">
                <span className="ab-card-tag">{side}</span>
                <span className="ab-card-label">{side === 'A' ? labelA : labelB}</span>
              </div>
              {busy && progress ? (
                <div className="ab-capturing">
                  <div className="ab-bar"><div className="ab-bar-fill" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
                  <div className="ab-capturing-row">
                    <span>{progress.phase === 'waiting' ? 'Waiting' : 'Checking'} {progress.done}/{progress.total}</span>
                    {progress.phase === 'waiting' && countdown != null && <span className="ab-next">next in {Math.ceil(countdown / 1000)}s</span>}
                  </div>
                  <button className="ab-btn ab-btn--stop" onClick={stop}>■ Stop</button>
                </div>
              ) : sample ? (
                <>
                  <div className="ab-mini">
                    <span className={`ab-mini-verdict ${stats.passRate === 1 ? 'ok' : stats.passRate === 0 ? 'bad' : 'mixed'}`}>
                      {stats.passes}/{stats.n} pass
                    </span>
                    <span className="ab-mini-loc">{stats.city || '?'}, {stats.state || '?'}</span>
                  </div>
                  <div className="ab-mini-sub">{sample.at} · {Object.keys(stats.riskFreq).length ? `${Object.keys(stats.riskFreq).length} risk rule(s) fired` : 'no risk rules fired'}</div>
                  <button className="ab-btn" onClick={() => capture(side)} disabled={!!capturing}>Re-capture {side}</button>
                </>
              ) : (
                <>
                  <div className="ab-empty">Not captured yet</div>
                  <button className="ab-btn ab-btn--primary" onClick={() => capture(side)} disabled={!!capturing}>▶ Capture {side}</button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {note && <div className="ab-note">{note}</div>}

      {/* Comparison */}
      {bothDone && <Comparison a={sampleA} b={sampleB} sa={statsA} sb={statsB} onExport={exportJson} onReset={reset} />}
      {(sampleA || sampleB) && !bothDone && !capturing && (
        <div className="ab-actions"><button className="ab-link" onClick={reset}>Reset</button></div>
      )}
    </div>
  );
}

function Comparison({ a, b, sa, sb, onExport, onReset }) {
  const distance = haversineMeters(sa.centroid, sb.centroid);
  const allRules = [...new Set([...Object.keys(sa.riskFreq), ...Object.keys(sb.riskFreq)])];

  // Plain-language verdict lines.
  const verdicts = [];
  for (const rule of allRules) {
    const av = sa.riskFreq[rule] || 0, bv = sb.riskFreq[rule] || 0;
    if (av !== bv) {
      const more = bv > av ? b.label : a.label;
      verdicts.push(`${RULE_LABELS[rule] || rule}: flagged ${Math.max(av, bv)}/${Math.max(sa.n, sb.n)} in "${more}" vs ${Math.min(av, bv)}/${Math.max(sa.n, sb.n)} in the other → BrowserGuard caught the difference.`);
    }
  }
  if (distance != null && distance > 1000) {
    verdicts.push(`Reported location moved ${formatDistance(distance)} between conditions (${sa.city || '?'} → ${sb.city || '?'}).`);
  }
  if (sa.accuracy != null && sb.accuracy != null && Math.abs(sa.accuracy - sb.accuracy) > 50) {
    verdicts.push(`Accuracy changed from ±${sa.accuracy} m to ±${sb.accuracy} m.`);
  }
  if (sa.passRate !== sb.passRate) {
    verdicts.push(`Pass rate ${pct(sa.passRate)} ("${a.label}") vs ${pct(sb.passRate)} ("${b.label}").`);
  }
  if (!verdicts.length) verdicts.push('No material difference detected between the two conditions.');

  const rows = [
    ['Pass rate', pct(sa.passRate), pct(sb.passRate)],
    ['Reported location', `${sa.city || '?'}, ${sa.state || '?'}`, `${sb.city || '?'}, ${sb.state || '?'}`],
    ['Avg accuracy', sa.accuracy != null ? `±${sa.accuracy} m` : '—', sb.accuracy != null ? `±${sb.accuracy} m` : '—'],
    ['Positional jitter', sa.jitter != null ? `${sa.jitter} m` : '—', sb.jitter != null ? `${sb.jitter} m` : '—'],
    ['VPN/proxy score', sa.proxyscore != null ? `${sa.proxyscore}%` : '—', sb.proxyscore != null ? `${sb.proxyscore}%` : '—'],
    ['Latency p50 / p95', sa.latency.p50 != null ? `${sa.latency.p50} / ${sa.latency.p95} ms` : '—', sb.latency.p50 != null ? `${sb.latency.p50} / ${sb.latency.p95} ms` : '—'],
  ];

  return (
    <div className="ab-compare">
      <div className="ab-compare-head">
        <span className="ab-compare-title">Comparison</span>
        <div className="ab-compare-actions">
          <button className="ab-link" onClick={onExport}>Export JSON</button>
          <button className="ab-link" onClick={onReset}>Reset</button>
        </div>
      </div>

      <table className="ab-table">
        <thead>
          <tr><th>Metric</th><th>A · {a.label}</th><th>B · {b.label}</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}><td>{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td></tr>
          ))}
        </tbody>
      </table>

      {allRules.length > 0 && (
        <div className="ab-risks">
          <div className="ab-risks-title">Risk rules fired (count / {Math.max(sa.n, sb.n)})</div>
          {allRules.map((rule) => {
            const av = sa.riskFreq[rule] || 0, bv = sb.riskFreq[rule] || 0;
            return (
              <div key={rule} className={`ab-risk-row${av !== bv ? ' ab-risk-row--diff' : ''}`}>
                <span className="ab-risk-name">{RULE_LABELS[rule] || rule}</span>
                <span className="ab-risk-counts"><b className={av ? 'fired' : ''}>{av}</b> · <b className={bv ? 'fired' : ''}>{bv}</b></span>
              </div>
            );
          })}
        </div>
      )}

      <div className="ab-verdict">
        <div className="ab-verdict-title">Verdict</div>
        {verdicts.map((v, i) => <div key={i} className="ab-verdict-line">• {v}</div>)}
        <div className="ab-verdict-note">n = {sa.n} / {sb.n} per side. Small samples — increase samples/side for stronger confidence.</div>
      </div>
    </div>
  );
}
